import { AccountClass } from "@common/constants/domains/accounting/accounts";
import {
    Counterparty,
    CounterpartySource,
    INTEGRATION_COUNTERPARTY_SOURCES,
} from "../types/counterparty";

export enum CounterpartyDirection {
    CUSTOMER = "customer",
    VENDOR = "vendor",
    NEUTRAL = "neutral",
}

export enum CounterpartyDirectionOverride {
    VENDOR = "vendor",
    CUSTOMER = "customer",
}

export enum RegisteredCounterpartyIncludeScope {
    ALL = "all",
    NONE = "none",
    USED = "used",
}

export enum LocalCounterpartyIncludeScope {
    ALL = "all",
    NONE = "none",
    FROM_WORKSPACE = "from_workspace",
}

export function getCounterpartyDirectionForAccountClass(
    accountClass?: AccountClass,
): CounterpartyDirection {
    switch (accountClass) {
        case AccountClass.income:
            return CounterpartyDirection.CUSTOMER;
        case AccountClass.expenses:
            return CounterpartyDirection.VENDOR;
        default:
            return CounterpartyDirection.NEUTRAL;
    }
}

/**
 *
 * @param counterparty
 * @returns true if the workspace is not set on the counterparty
 */
export function isRegisteredCounterparty(
    counterparty: Pick<Counterparty, "workspaceId">,
): boolean {
    return !counterparty.workspaceId;
}

export function isIntegrationCounterpartySource(
    source: CounterpartySource | null | undefined,
): boolean {
    return source != null && INTEGRATION_COUNTERPARTY_SOURCES.includes(source);
}

export const COUNTERPARTY_GLOBAL_SOURCE_LABEL = "Kick";
export const COUNTERPARTY_NULL_SOURCE_LABEL = "Custom";

export const COUNTERPARTY_SOURCE_LABELS: Record<CounterpartySource, string> = {
    [CounterpartySource.MANUAL]: COUNTERPARTY_NULL_SOURCE_LABEL,
    [CounterpartySource.ENRICHMENT]: COUNTERPARTY_GLOBAL_SOURCE_LABEL,
    [CounterpartySource.RAMP]: "Ramp",
    [CounterpartySource.STRIPE]: "Stripe",
    [CounterpartySource.MERCURY]: "Mercury",
    [CounterpartySource.QUICKBOOKS]: "QuickBooks",
    [CounterpartySource.BILLCOM]: "BILL",
};

export function getCounterpartySourceLabel(
    counterparty: Pick<Counterparty, "workspaceId" | "source">,
): string {
    if (counterparty.workspaceId === null) {
        return COUNTERPARTY_GLOBAL_SOURCE_LABEL;
    }
    if (counterparty.source === null) {
        return COUNTERPARTY_GLOBAL_SOURCE_LABEL;
    }
    return COUNTERPARTY_SOURCE_LABELS[counterparty.source];
}

export function getCounterpartyCreatedByLabel(
    counterparty: Pick<
        Counterparty,
        "workspaceId" | "updatedByUserName" | "source"
    >,
): string {
    if (counterparty.workspaceId === null) {
        return COUNTERPARTY_GLOBAL_SOURCE_LABEL;
    }
    // The creating user's name is only shown for manually-added counterparties;
    // a null source is treated as manual (untracked/legacy local entries).
    // Integration- and enrichment-sourced counterparties always show their
    // source label instead of whoever last touched them.
    if (
        counterparty.source === null ||
        counterparty.source === CounterpartySource.MANUAL
    ) {
        return counterparty.updatedByUserName ?? COUNTERPARTY_NULL_SOURCE_LABEL;
    }
    return getCounterpartySourceLabel(counterparty);
}

export function buildCounterpartySourceLabelSql(alias: string): string {
    const sourceBranches = Object.entries(COUNTERPARTY_SOURCE_LABELS)
        .map(
            ([source, label]) =>
                `WHEN ${alias}.source = '${source}' THEN '${label}'`,
        )
        .join("\n            ");
    return `CASE
            WHEN ${alias}."workspaceId" IS NULL THEN '${COUNTERPARTY_GLOBAL_SOURCE_LABEL}'
            ${sourceBranches}
            ELSE '${COUNTERPARTY_GLOBAL_SOURCE_LABEL}'
        END`;
}

/**
 * SQL expression that mirrors {@link getCounterpartyCreatedByLabel}: global
 * counterparties resolve to "Kick"; manual/null-source local ones to the
 * creating user's name (`preferredName ?? legalName`), falling back to "Custom"
 * when no name is set; integration- and enrichment-sourced local ones to their
 * source label (see {@link buildCounterpartySourceLabelSql}). Used to keep the
 * "Created by" column sortable by the displayed value.
 */
export function buildCounterpartyCreatedByLabelSql(alias: string): string {
    return `CASE
            WHEN ${alias}."workspaceId" IS NULL THEN '${COUNTERPARTY_GLOBAL_SOURCE_LABEL}'
            WHEN ${alias}."source" IS NULL OR ${alias}."source" = '${CounterpartySource.MANUAL}' THEN COALESCE(NULLIF((
                SELECT COALESCE(u."preferredName", u."legalName", '')
                FROM "user" u
                WHERE u.id = ${alias}."updatedByUserId"
            ), ''), '${COUNTERPARTY_NULL_SOURCE_LABEL}')
            ELSE ${buildCounterpartySourceLabelSql(alias)}
        END`;
}

/**
 * SQL boolean condition selecting the counterparties the "Created by" column
 * labels as "Kick" (see {@link getCounterpartyCreatedByLabel}): global
 * counterparties (`workspaceId` null) and enrichment-sourced local
 * counterparties. Manual/null-source local counterparties label as the creating
 * member (or "Custom"), and integration-sourced ones as their source label, so
 * none of those belong here. Kept alongside the label builder so the filter and
 * the displayed value stay in sync.
 */
export function buildCounterpartyCreatedByKickConditionSql(
    alias: string,
): string {
    return `(
        ${alias}."workspaceId" IS NULL
        OR ${alias}."source" = '${CounterpartySource.ENRICHMENT}'
    )`;
}

export function isVerifiedCounterparty(
    counterparty: Pick<
        Counterparty,
        "workspaceId" | "updatedByUserId" | "source"
    >,
) {
    return Boolean(
        isRegisteredCounterparty(counterparty) ||
        counterparty.updatedByUserId ||
        isIntegrationCounterpartySource(counterparty.source),
    );
}

export function shouldShowCounterparty(
    counterparty: Pick<
        Counterparty,
        "workspaceId" | "updatedByUserId" | "source" | "shouldBeNormalized"
    >,
    treatLocalCounterpartiesAsVerified?: boolean,
) {
    return Boolean(
        isVerifiedCounterparty(counterparty) ||
        (treatLocalCounterpartiesAsVerified &&
            !counterparty.shouldBeNormalized),
    );
}
